import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import { useQueryStateContext, useVisualComponentsContext } from '../../../contexts';
import { useEventFieldOptions } from './useEventFieldOptions';
import { ClientDataType } from '../../../types';
import { useMappings } from '../../../hooks/queries';
import { useEventFieldplaceholder } from './useEventFieldPlaceholder';

type SingleEventFieldSelectProps = {
  onChange: (value: string) => void;
  value: string;
  dataType: ClientDataType;
  isInvalid?: boolean;
  hasWarning?: boolean;
  isOpenByDefault?: boolean;
};

export const EventFieldSelect = ({
  onChange,
  value,
  dataType,
  isInvalid,
  hasWarning,
  isOpenByDefault,
}: SingleEventFieldSelectProps) => {
  const [debouncedSearch, search, setSearch] = useDebouncedState('', 300);
  const { ConditionFieldSelect: VisualComponent, ConditionValueLoadingSelect: LoadingComponent } =
    useVisualComponentsContext();
  const { isReadOnly = false, esIndex } = useQueryStateContext();
  const { mappings, isLoading, didError } = useMappings({ dataType });
  const isUserField = dataType === ClientDataType.User;
  const eventFieldPlaceholder = useEventFieldplaceholder(isUserField, esIndex);

  const options = useEventFieldOptions({
    search: debouncedSearch,
    mappings,
    dataType,
  });

  const isDisabled = didError || isReadOnly;
  const hasError = isInvalid || didError;

  return (
    <>
      {isLoading ? (
        <LoadingComponent />
      ) : (
        <VisualComponent
          dataType={dataType}
          hasError={hasError}
          hasWarning={hasWarning}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isOpenByDefault={isOpenByDefault}
          onChange={(val) => {
            // if value is not in the options revert to previous value
            const commitOption = options.find((opt) => opt.value === val) ? val : value;
            if (commitOption) onChange(commitOption);
          }}
          options={options}
          placeholder={eventFieldPlaceholder}
          search={search}
          setSearch={setSearch}
          value={value}
          isVirtualized
        />
      )}
    </>
  );
};
